export type CrowdLevel = "empty" | "quiet" | "moderate" | "busy" | "packed";

export type SeasonPhase = "early" | "shoulder" | "peak" | "late";

export type DailyCrowdInput = {
  schoolHolidayPressure: number;
  isPublicHoliday: boolean;
  dayOfWeek: number;
  seasonPhase: SeasonPhase;
};

export type DailyCrowd = {
  level: CrowdLevel;
  score: number;
  note: string;
};

const DAY_WEIGHTS: Record<number, number> = {
  0: 0.15, // Sunday
  1: 0,
  2: 0,
  3: 0,
  4: 0,
  5: 0.05, // Friday
  6: 0.18, // Saturday
};

export const SEASON_PHASE_WEIGHTS: Record<SeasonPhase, number> = {
  early: 0.03,
  shoulder: 0.06,
  peak: 0.15,
  late: 0.03,
};

const PUBLIC_HOLIDAY_BOOST = 0.2;

export function levelFromScore(score: number): CrowdLevel {
  if (score < 0.15) return "empty";
  if (score < 0.3) return "quiet";
  if (score <= 0.55) return "moderate";
  if (score <= 0.8) return "busy";
  return "packed";
}

function buildNote(input: DailyCrowdInput, level: CrowdLevel): string {
  const isWeekend = input.dayOfWeek === 0 || input.dayOfWeek === 6;
  if (input.isPublicHoliday && isWeekend) return "Bank holiday weekend";
  if (input.isPublicHoliday) return "Public holiday";
  if (input.schoolHolidayPressure >= 0.4 && input.dayOfWeek === 6) return "School holiday Saturday";
  if (input.schoolHolidayPressure >= 0.3) return "School holidays";
  if (input.dayOfWeek === 6) return "Saturday";
  if (input.dayOfWeek === 0) return "Sunday";
  if (input.dayOfWeek === 5) return "Friday";
  if (level === "empty" || level === "quiet") return "Midweek";
  return "";
}

export function dailyCrowdPressure(input: DailyCrowdInput): DailyCrowd {
  const holiday = input.schoolHolidayPressure;
  const publicHol = input.isPublicHoliday ? PUBLIC_HOLIDAY_BOOST : 0;
  const day = DAY_WEIGHTS[input.dayOfWeek] ?? 0;
  const season = SEASON_PHASE_WEIGHTS[input.seasonPhase];
  const raw = holiday + publicHol + day + season;
  const score = Math.min(1, raw);
  const level = levelFromScore(score);
  const note = buildNote(input, level);
  return { level, score, note };
}

export function crowdColor(level: CrowdLevel): string {
  switch (level) {
    case "empty": return "bg-emerald-200";
    case "quiet": return "bg-lime-200";
    case "moderate": return "bg-amber-200";
    case "busy": return "bg-orange-300";
    case "packed": return "bg-red-300";
  }
}

export function crowdTextColor(level: CrowdLevel): string {
  switch (level) {
    case "empty": return "text-emerald-700";
    case "quiet": return "text-lime-700";
    case "moderate": return "text-amber-700";
    case "busy": return "text-orange-700";
    case "packed": return "text-red-700";
  }
}

